import { dateBackendDateTimeToRelativeTime } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { ResourceHeadersPropsWithActions } from 'modules/core/lib/navigation/types'
import { Element } from 'modules/forms/lib/Element'
import { Form } from 'modules/forms/lib/Form'
import { SectionElement } from 'modules/forms/lib/elements/SectionElement'

function isSectionElement(el: Element): el is SectionElement {
  return el instanceof SectionElement
}

// Shared helper to build metadata items from common fields
function buildMetadata({
  authorFullName,
  sectionsCount,
  questionsCount,
  updatedAt,
}: {
  authorFullName: string | null | undefined
  sectionsCount: number
  questionsCount: number
  updatedAt: string | null | undefined
}) {
  const metadata: NonNullable<ResourceHeadersPropsWithActions['metadata']> = []

  if (authorFullName) {
    metadata.push({
      label: i18n.t('procurement.task_forms.header.metadata.created_by'),
      value: { type: 'text' as const, content: authorFullName },
    })
  }

  metadata.push({
    label: i18n.t('procurement.task_forms.header.metadata.sections'),
    value: { type: 'text' as const, content: sectionsCount.toString() },
  })

  metadata.push({
    label: i18n.t('procurement.task_forms.header.metadata.questions'),
    value: { type: 'text' as const, content: questionsCount.toString() },
  })

  if (updatedAt) {
    metadata.push({
      label: i18n.t('procurement.task_forms.header.metadata.last_edited'),
      value: {
        type: 'text' as const,
        content: dateBackendDateTimeToRelativeTime(updatedAt),
      },
    })
  }

  return metadata
}

function buildActions(
  status: string,
  formId: string | undefined
): Pick<ResourceHeadersPropsWithActions, 'primaryAction' | 'secondaryActions' | 'otherActions'> {
  return {
    primaryAction:
      status === 'draft'
        ? {
            key: 'publish',
            label: i18n.t('common.publish'),
          }
        : undefined,
    secondaryActions: formId ? [{ key: 'preview', label: i18n.t('common.preview') }] : undefined,
    otherActions: [
      {
        key: 'duplicate',
        label: i18n.t('procurement.purchase_requests.settings.task_forms.actions.duplicate'),
      },
    ],
  }
}

/** Used in the route's `meta` to provide the initial `headerComponentProps`. */
export type TaskFormRouteFormData = {
  id: string
  title: string
  description?: string | null
  status: string
  updatedAt?: string | null
  author?: { employee?: { fullName?: string | null } | null } | null
  elements?: ReadonlyArray<{ type: string }> | null
}

export function buildTaskFormRouteHeaderProps(
  form: TaskFormRouteFormData
): ResourceHeadersPropsWithActions {
  const elements = form.elements ?? []
  const sectionsCount = elements.filter((e) => e.type === 'section').length
  const questionsCount = elements.filter((e) => e.type !== 'section').length
  const statusText =
    form.status === 'active'
      ? i18n.t('forms.custom_templates.data_collection.status_values.published')
      : i18n.t('forms.custom_templates.data_collection.status_values.draft')

  return {
    title: form.title,
    description: form.description ?? undefined,
    status: {
      label: statusText,
      text: statusText,
      variant: form.status === 'active' ? 'positive' : 'neutral',
    },
    metadata: buildMetadata({
      authorFullName: form.author?.employee?.fullName,
      sectionsCount,
      questionsCount,
      updatedAt: form.updatedAt,
    }),
    ...buildActions(form.status, form.id),
  }
}

function getSectionsCount(elements: Element[]): number {
  return elements.filter(isSectionElement).length
}

function getQuestionsCount(elements: Element[]): number {
  const sectionElements = elements.filter(isSectionElement)
  const rootQuestions = elements.length - sectionElements.length
  const sectionQuestions = sectionElements.reduce(
    (acc, section) => acc + section.elements.length,
    0
  )
  return rootQuestions + sectionQuestions
}

interface BuildTaskFormHeaderPropsOptions {
  form: Form
  formId: string | undefined
}

/** Used in the page component to keep the header in sync after form loads/updates. */
function buildTaskFormHeaderProps({
  form,
  formId,
}: BuildTaskFormHeaderPropsOptions): ResourceHeadersPropsWithActions {
  const statusText =
    form.status === 'active'
      ? i18n.t('forms.custom_templates.data_collection.status_values.published')
      : i18n.t('forms.custom_templates.data_collection.status_values.draft')

  return {
    title: form.title,
    description: form.description ?? undefined,
    status: {
      label: statusText,
      text: statusText,
      variant: form.status === 'active' ? 'positive' : 'neutral',
    },
    metadata: buildMetadata({
      authorFullName: form.authorFullName,
      sectionsCount: getSectionsCount(form.elements ?? []),
      questionsCount: getQuestionsCount(form.elements ?? []),
      updatedAt: form.updatedAt,
    }),
    ...buildActions(form.status, formId),
  }
}
